import { useAuthStore } from '@/stores/authStore';
import { ROLES } from '@/config/permissions';

/**
 * usePermissions - the RBAC->PBAC migration's centralized frontend authorization utility.
 * Every component should call this instead of writing raw `currentUser.roles.includes(...)` checks or
 * `role === 'SUPER_ADMIN'` special-casing - this is the ONE place SUPER_ADMIN's "has every permission"
 * behavior lives, and the one place a future permission-source change (e.g. a live /me refetch instead
 * of JWT decode) needs to be made.
 *
 * IMPORTANT: this only improves the interface by hiding controls a user can't use - it is NOT a
 * security boundary. The backend independently enforces every permission check (hasAuthority(...)) on
 * every request regardless of what this hook returns.
 */
export function usePermissions() {
  const currentUser = useAuthStore((s) => s.currentUser);
  const permissions = currentUser?.permissions ?? [];
  const roles = currentUser?.roles ?? [];
  const isSuperAdmin = roles.includes(ROLES.SUPER_ADMIN);

  const hasPermission = (permission: string): boolean => isSuperAdmin || permissions.includes(permission);
  const hasAnyPermission = (required: string[]): boolean =>
    isSuperAdmin || required.length === 0 || required.some((p) => permissions.includes(p));
  const hasAllPermissions = (required: string[]): boolean =>
    isSuperAdmin || required.every((p) => permissions.includes(p));
  const hasRole = (role: string): boolean => roles.includes(role);

  return { permissions, roles, isSuperAdmin, hasPermission, hasAnyPermission, hasAllPermissions, hasRole };
}
